import React, { useState } from "react";
import api from "../../api/api";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";

import LockResetIcon from "@mui/icons-material/LockReset";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { email, code } = location.state || {};

  const handleReset = async (e) => {
    e.preventDefault();

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/set-new-password/", {
        email,
        code,
        new_password: newPassword,
      });

      setMessage(
        res.data.message ||
          "Password reset successful. Redirecting..."
      );

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setMessage(
        err.response?.data?.error ||
          "Password reset failed."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "#fff",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,

        "@keyframes float1": {
          "0%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(-35px)" },
          "100%": { transform: "translateY(0px)" },
        },

        "@keyframes float2": {
          "0%": { transform: "translateY(0px) translateX(0px)" },
          "50%": {
            transform: "translateY(25px) translateX(-20px)",
          },
          "100%": {
            transform: "translateY(0px) translateX(0px)",
          },
        },

        "@keyframes float3": {
          "0%": { transform: "translateY(0px) translateX(0px)" },
          "50%": {
            transform: "translateY(-20px) translateX(20px)",
          },
          "100%": {
            transform: "translateY(0px) translateX(0px)",
          },
        },

        "@keyframes twinkle": {
          "0%": { opacity: 0.2 },
          "50%": { opacity: 1 },
          "100%": { opacity: 0.2 },
        },
      }}
    >
      {/* LARGE BUBBLES */}

      <Box
        sx={{
          position: "absolute",
          width: { xs: 180, md: 320 },
          height: { xs: 180, md: 320 },
          borderRadius: "50%",
          background: "rgba(255,140,0,.14)",
          top: "5%",
          left: "-5%",
          animation: "float1 14s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 220, md: 360 },
          height: { xs: 220, md: 360 },
          borderRadius: "50%",
          background: "rgba(33,150,243,.12)",
          top: "55%",
          right: "-10%",
          animation: "float2 18s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 170, md: 260 },
          height: { xs: 170, md: 260 },
          borderRadius: "50%",
          background: "rgba(255,183,77,.18)",
          bottom: "-5%",
          left: "25%",
          animation: "float3 20s ease-in-out infinite",
        }}
      />

      {/* MEDIUM BUBBLES */}

      <Box
        sx={{
          position: "absolute",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(144,164,174,.15)",
          top: "15%",
          right: "15%",
          animation: "float2 12s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "rgba(255,140,0,.12)",
          bottom: "15%",
          left: "10%",
          animation: "float1 16s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 140,
          height: 140,
          borderRadius: "50%",
          background: "rgba(33,150,243,.12)",
          top: "40%",
          left: "8%",
          animation: "float3 15s ease-in-out infinite",
        }}
      />

      {/* STARS */}

      {[...Array(40)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: `${2 + (i % 3)}px`,
            height: `${2 + (i % 3)}px`,
            borderRadius: "50%",
            background:
              i % 4 === 0
                ? "#ff8c00"
                : i % 4 === 1
                ? "#2196f3"
                : i % 4 === 2
                ? "#ffb74d"
                : "#90a4ae",
            top: `${(i * 13) % 100}%`,
            left: `${(i * 17) % 100}%`,
            opacity: 0.6,
            animation: `twinkle ${3 + (i % 4)}s infinite`,
          }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        style={{
          width: "100%",
          maxWidth: "550px",
          zIndex: 2,
        }}
      >
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: "30px",
            background: "rgba(255,255,255,.92)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,140,0,.12)",
            boxShadow:
              "0 20px 60px rgba(0,0,0,.08)",
          }}
        >
          <Box textAlign="center">
            <motion.div
              animate={{
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                repeat: Infinity,
                duration: 3,
              }}
            >
              <LockResetIcon
                sx={{
                  fontSize: 80,
                  color: "#ff8c00",
                }}
              />
            </motion.div>

            <Typography
              variant="h4"
              fontWeight={800}
              mt={2}
            >
              Create New Password
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: "#666",
              }}
            >
              Set a secure password for your
              Zuluwhiskey Shipping account.
            </Typography>
          </Box>

          <Alert
            severity="info"
            sx={{
              mt: 3,
              borderRadius: 3,
            }}
          >
            Use at least 8 characters with a mix of
            letters, numbers, and symbols.
          </Alert>

          <Box
            component="form"
            onSubmit={handleReset}
            sx={{
              mt: 3,
            }}
          >
            <TextField
              fullWidth
              required
              label="New Password"
              type={
                showPassword ? "text" : "password"
              }
              value={newPassword}
              disabled={loading}
              onChange={(e) =>
                setNewPassword(e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon
                      sx={{
                        color: "#ff8c00",
                      }}
                    />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowPassword(
                          !showPassword
                        )
                      }
                    >
                      {showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.7,
                borderRadius: "14px",
                fontWeight: 700,
                background:
                  "linear-gradient(135deg,#ff8c00,#ffb74d)",
                color: "#fff",

                "&:hover": {
                  background:
                    "linear-gradient(135deg,#ff9800,#ff8c00)",
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "#fff",
                  }}
                />
              ) : (
                "Reset Password"
              )}
            </Button>

            <Button
              fullWidth
              startIcon={<ArrowBackIcon />}
              onClick={() =>
                navigate("/forgotPassword")
              }
              sx={{
                mt: 2,
                color: "#555",
              }}
            >
              Back
            </Button>
          </Box>

          {message && (
            <Typography
              textAlign="center"
              mt={3}
              fontWeight={600}
              color={
                message.includes("successful")
                  ? "green"
                  : "#ff8c00"
              }
            >
              {message}
            </Typography>
          )}

          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #eee",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                color: "#777",
                fontSize: ".9rem",
              }}
            >
              🚢 Zuluwhiskey Shipping • Fast • Secure •
              Reliable
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default ResetPassword;